import React, { useEffect, useState } from 'react';
import { requestApi } from 'utils/request';
import { EstimatedAmountManagePage } from './style';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../layout';
import SelectBox from 'components/SelectBox';
import ROUTE_PATH from '../../../routes/config';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import dayjs from 'dayjs';
import { formatNumberWithCommas } from '../../../tools/NumberTool';
import { useUI } from '../../../contexts/UIContext';

const EstimatedAmountManage = () => {
  const navigate = useNavigate();
  const { setContextLoading, setModalData } = useUI();
  const [loansMallData, setLoansMallData] = useState({});
  const [filterMallList, setFilterMallList] = useState([]);
  const [apiCallTime, setApiCallTime] = useState('');
  const [selectedMall, setSelectedMall] = useState(0);

  const getLinkageState = (
    state,
    amount,
    mallName,
    businessRegistrationNumber,
    dataProviderSeq,
    mallSeq,
    mallAccountId,
    isLatestDataYn
  ) => {
    switch (state) {
      case 'FINISH':
        if (isLatestDataYn === 'Y') {
          return (
            <span
              style={{ color: '#009FF9', cursor: 'pointer' }}
              onClick={() =>
                navigate(ROUTE_PATH.mypage.estimatedAmountDetail, {
                  state: { dataProviderSeq, mallSeq, mallAccountId },
                })
              }
            >
              {amount.toLocaleString()} 원{' '}
              <FeatherIcon
                icon="chevron-right"
                size={20}
                stroke="#009FF9"
                strokeWidth={2}
              />
            </span>
          );
        } else {
          return (
            <span style={{ color: '#6F727A' }}>
              최신 데이터 없음{' '}
              <button
                type="button"
                onClick={() => {
                  setModalData({
                    textAlign: 'left',
                    fontWeight: 400,
                    contents: `오늘 기준 최신 수집데이터가 없습니다.\n최신 데이터를 조회하려면 1:1문의를 이용해 주세요.`,
                    cancelBtnName: '닫기',
                    confirmBtnName: '1:1 문의하기',
                    onClickConfirmBtn: () =>
                      navigate(ROUTE_PATH.serviceCenter.auth.create),
                  });
                }}
              >
                <FeatherIcon
                  icon="help-circle"
                  size={16}
                  stroke="#6F727A"
                  strokeWidth={2}
                />
              </button>
            </span>
          );
        }

      case 'WAIT':
        return <span style={{ color: '#6F727A' }}>연동대기</span>;
      case 'FAIL':
        return (
          <span style={{ color: '#F50E0E' }}>
            연동실패{' '}
            <button
              type="button"
              onClick={() => {
                setModalData({
                  textAlign: 'left',
                  fontWeight: 400,
                  contents: `연동이 원할하지 않거나 인증 정보가 일치하지 않아 판매몰 연동에 실패했습니다.\n자세한 사유는 [데이터 연동 관리]에서 확인 후, 다시 연동을 진행해주세요.`,
                  cancelBtnName: '닫기',
                  confirmBtnName: '데이터 연동 관리',
                  onClickConfirmBtn: () =>
                    navigate(ROUTE_PATH.mypage.dataLinkageManage),
                });
              }}
            >
              <FeatherIcon
                icon="help-circle"
                size={16}
                stroke="#F50E0E"
                strokeWidth={2}
              />
            </button>
          </span>
        );
      case 'STOP':
        return <span style={{ color: '#6F727A' }}>연동중지</span>;
      case 'UNAVAILABLE':
        return (
          <span style={{ color: '#F50E0E' }}>
            연동불가{' '}
            <button
              type="button"
              onClick={() => {
                setModalData({
                  textAlign: 'left',
                  fontWeight: 400,
                  contents: `가입 사업자 번호와 연동하려는 판매몰의 사업자번호가 불일치합니다.\n회원가입한 사업자 번호와 데이터 연동을 진행하는 판매몰에 가입된 사업자 번호는 일치해야 연동이 가능 합니다.\n• ${mallName} 판매몰의 가입된 사업자 번호는 ${businessRegistrationNumber} 입니다.`,
                  cancelBtnName: '닫기',
                  confirmBtnName: '1:1 문의하기',
                  onClickConfirmBtn: () =>
                    navigate(ROUTE_PATH.serviceCenter.auth.create),
                });
              }}
            >
              <FeatherIcon
                icon="help-circle"
                size={16}
                stroke="#F50E0E"
                strokeWidth={2}
              />
            </button>
          </span>
        );
      default:
        return null;
    }
  };

  const onChangeSelectMall = event => {
    const { value } = event.target;
    setSelectedMall(value);
  };

  const fetchManageMall = async mallSeq => {
    try {
      const res = await requestApi({
        method: 'GET',
        url: `/v1/au/data-provider/manage/mall`,
        params: {
          mallSeq: mallSeq,
        },
      });
      if (res.success) {
        setLoansMallData(res.data.result);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setApiCallTime(dayjs().format('YYYY.MM.DD HH:mm:ss'));
      setContextLoading(false);
    }
  };

  const fetchMallFilter = async (dataProviderSeq = 0) => {
    try {
      const res = await requestApi({
        method: 'GET',
        url: `/v1/au/common/business/data-provider/${dataProviderSeq}/mall/list`,
      });

      const mappedData = res.data.result.map(item => ({
        label: item.mallName,
        value: item.mallSeq,
      }));
      mappedData.unshift({ label: '판매몰 전체', value: 0 });
      setFilterMallList(mappedData);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchManageMall(selectedMall);
    })();
  }, [selectedMall]);

  useEffect(() => {
    (async () => {
      await fetchMallFilter();
    })();
  }, []);

  return (
    <Layout pageTitle="정산예정금 관리">
      <EstimatedAmountManagePage>
        <section className="summeryBox">
          <dl>
            <dt>정산예정금 합계</dt>
            <dd>
              {formatNumberWithCommas(loansMallData.sumSettlementScheduledAmt)}{' '}
              <span>원</span>
            </dd>
          </dl>
          <button
            type="button"
            className="btnAccountView"
            onClick={() => navigate(ROUTE_PATH.mypage.estimatedAmountAccount)}
          >
            계좌별 정산예정금 보기
          </button>
          <button
            type="button"
            className="btnRefresh"
            onClick={() => fetchManageMall(selectedMall)}
          >
            <FeatherIcon
              icon="refresh-ccw"
              size={12}
              stroke="#222"
              strokeWidth={2}
            />{' '}
            {apiCallTime} 기준
          </button>
        </section>
        <section className="listWrapper">
          {loansMallData.mallInfo?.length > 0 ? (
            <>
              <div className="listHeader">
                <div className="total">
                  총 <span>{loansMallData.mallCount}</span>개
                </div>
                <SelectBox
                  optionHandleChange={onChangeSelectMall}
                  optionList={filterMallList}
                  name="filterMallList"
                  isNotDisabled={true}
                  noPadding={true}
                  $gray
                  width={'180px'}
                  style={{
                    fontSize: '1.3rem',
                  }}
                />
              </div>
              <ul>
                {loansMallData.mallInfo?.map((item, index) => {
                  return (
                    <li key={index}>
                      <dl>
                        <dt className="top">{item.mallName}</dt>
                        <dd className="top">
                          {getLinkageState(
                            item.mallInterlockStatusCd,
                            item.sumSettlementScheduledAmt,
                            item.mallName,
                            item.businessRegistrationNumber,
                            item.dataProviderSeq,
                            item.mallSeq,
                            item.mallAccountId,
                            item.isLatestDataYn
                          )}
                        </dd>
                        <dt>판매몰 아이디</dt>
                        <dd>{item.mallAccountId}</dd>
                        <dt>최근 1년 매출 합계</dt>
                        <dd>
                          {item.mallInterlockStatusCd === 'FINISH' &&
                          item.isLatestDataYn === 'Y'
                            ? `${item.sumSalesAmt.toLocaleString()} 원`
                            : '데이터 없음'}
                        </dd>
                      </dl>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <p className="noList">연동 가능한 정산예정금이 없습니다.</p>
          )}
        </section>
      </EstimatedAmountManagePage>
    </Layout>
  );
};
export default EstimatedAmountManage;
